import type { ModelDetails, UserModels } from '~/backend/types';

const DEFAULT_CUSTOMIZATION_NAME = 'default';

export type PredictionModel = {
    name: string;
    bodyPart: string;
    label: string;
    type: string;
    tags: string[];
    inputModalities: string[];
    outputModality?: string;
    isFreeTextEntryModel: boolean;
    version: string;
    isLatest: boolean;
    customizations?: {
        name: string;
        label: string;
    }[];
};

export function formatModalityHint(model: {
    inputModalities?: string[];
    outputModality?: string;
}): string | undefined {
    const input = model.inputModalities?.join('/') || undefined;
    if (input && model.outputModality) {
        return `${input} → ${model.outputModality}`;
    }
    return input ?? model.outputModality;
}

export function transformPredictionModelData(
    src: ModelDetails
): PredictionModel {
    const {
        model_name,
        body_part,
        model_label,
        model_type,
        model_tags,
        input_modalities = [],
        output_modality,
        customization_names,
        version,
        is_latest,
    } = src;

    const customizations = customization_names
        .map((customizationName) => ({
            name: `${model_name}${
                customizationName === DEFAULT_CUSTOMIZATION_NAME
                    ? ''
                    : `.${customizationName}`
            }`,
            label: `${model_label}${
                customizationName === DEFAULT_CUSTOMIZATION_NAME
                    ? ''
                    : ` (${customizationName})`
            }`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

    return {
        name: model_name,
        bodyPart: body_part,
        type: model_type,
        tags: model_tags,
        inputModalities: input_modalities,
        outputModality: output_modality,
        isFreeTextEntryModel: false,
        label: model_label,
        customizations,
        version: version || '',
        isLatest: is_latest || false,
    };
}

export function transformAvailableModelsData(
    src: UserModels
): PredictionModel[] {
    if (src.user_model_details) {
        return src.user_model_details
            .filter(
                (model) =>
                    model.visibility === 'always_show' ||
                    (model.visibility !== 'always_hide' && !model.deprecated)
            )
            .map((m) => transformPredictionModelData(m));
    }

    return [];
}
